import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ResultPage from './ResultPage/ResultPage';
import SearchInput from './SearchInput';
import SearchResultList from './SearchResultList';
import NoSearchResults from './NoSearchResults/NoSearchResults';
import { trackSearchItemSelected } from '../../../analytics/searchEventAnalytics';
import * as SessionAnalyticsService from 'services/SessionAnalyticsService/SessionAnalyticsService';
import * as searchTypes from '../../../constants/searchTypes';
import { ResultsHeader } from '../common/PageLayout.styles';
import {
  BlueSection,
  ErrorMessage,
  ResultsBox,
  SearchErrorWrapper,
  SearchAllBox,
  SearchAllResults,
  SearchOrdersPageWrapper,
  LoadingContainer,
} from './Search.styles';
import {
  extractFirstMessage,
  extractSelectedTicketFromState,
  getSelectedSearchResult,
  isSpecialist,
  getPartnerPlatformHistoricalTicketsFromState,
  isPartnerPlatformTicketsSearchEnabled,
} from '../../../extractFromState';
import {
  getSearchAllError,
  getSearchAllKBResults,
  getSearchAllMacroResults,
  getSearchAllQuery,
  getSearchAllTicketResults,
  noSearchAllResultsAndQueryIsEmpty,
  selectToolGeneralSearchType,
} from '../../../selectors/tools';
import { bindActionCreators } from 'redux';
import { setLoading } from '../../../actions';
import {
  searchAll,
  selectSearchResultTicket,
  searchInPartersTicketingSystem,
  selectParnerPlatformTicketForView,
} from '../../../actions/searchActions';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import Loading from '../../LoadingComponent/LoadingComponent';

class SearchAll extends Component {
  // TODO: Move fields into Redux since other components need it
  state = {
    userSearchQuery: '',
    autoSearchQuery: '',
    partnerPlatformTicketsLoading: false,
  };

  async componentDidMount() {
    if (!this.props.isSpecialist) {
      return;
    }
  }

  toggleLocalSpinnerForPartnerPlatformTickets = (value) => {
    // if `value` isn't passed, just toggle
    const loading = typeof value === 'undefined' ? !this.state.partnerPlatformTicketsLoading : value;

    return this.setState({ partnerPlatformTicketsLoading: loading });
  };

  handleSearchAll = (searchValue) => {
    this.props.setLoading(true);
    this.toggleLocalSpinnerForPartnerPlatformTickets(true); // local spinner

    // `searchAll` algolia search, fast;
    // `searchInPartersTicketingSystem` search in ticketing system (ZENDESK), sometimes more accurate, but slower;
    this.props.searchInPartersTicketingSystem(
      searchValue,
      this.props.selectedTicket,
      this.toggleLocalSpinnerForPartnerPlatformTickets
    );
    this.props.searchAll(searchValue, true);

    this.setState({
      userSearchQuery: searchValue,
    });
  };

  selectResult = (index, resultType) => {
    this.props.selectResult(resultType, index);
  };

  handlePartnerPlatformTicketSelection = (index) => {
    const selectedParnerPlatformTicket = this.props.partnerPlatformHistoricalTickets[index];
    const { selectedTicket } = this.props;

    this.props.selectParnerPlatformTicketForView(selectedParnerPlatformTicket, selectedTicket, index);
  };

  showPartnerTicketSearchResults() {
    return (
      this.props.isPartnerPlatformTicketsSearchEnabled &&
      !isEmpty(this.props.partnerPlatformHistoricalTickets) &&
      !this.state.partnerPlatformTicketsLoading
    );
  }

  renderResults() {
    const {
      macroSearchResults,
      kbSearchResults,
      ticketSearchResults,
      ticketSearchError,
      partnerPlatformHistoricalTickets,
    } = this.props;

    let results = [];
    if (macroSearchResults.length || kbSearchResults.length || ticketSearchResults.length) {
      if (macroSearchResults.length) {
        results.push(
          <div key={'macroSearch'}>
            <ResultsHeader>Macros</ResultsHeader>
            <SearchResultList
              listType={searchTypes.SEARCH_MACROS}
              resultList={macroSearchResults}
              resultClickAction={(index, type) => {
                this.selectResult(index, type);
                trackSearchItemSelected();
              }}
            />
          </div>
        );
      }

      if (kbSearchResults.length) {
        results.push(
          <div key={'kBSearch'}>
            <ResultsHeader>Knowledge base articles</ResultsHeader>
            <SearchResultList
              listType={searchTypes.SEARCH_KB}
              resultList={kbSearchResults}
              resultClickAction={(index, type) => {
                this.selectResult(index, type);
                trackSearchItemSelected();
              }}
            />
          </div>
        );
      }

      if (this.showPartnerTicketSearchResults()) {
        results.push(
          <div key={'partnerHistoricalSearch'}>
            <ResultsHeader>Partner's historical tickets</ResultsHeader>
            <SearchResultList
              listType={searchTypes.SEARCH_TICKETS}
              resultList={partnerPlatformHistoricalTickets}
              resultClickAction={(index) => this.handlePartnerPlatformTicketSelection(index)}
              hideDeleteButton
            />
          </div>
        );
      }

      if (ticketSearchResults.length) {
        results.push(
          <div key={'ticketSearch'}>
            <ResultsHeader>Tickets</ResultsHeader>
            <SearchResultList
              listType={searchTypes.SEARCH_TICKETS}
              resultList={ticketSearchResults}
              resultClickAction={(index, type) => {
                this.selectResult(index, type);
                trackSearchItemSelected();
              }}
            />
          </div>
        );
      }

      return (
        <SearchAllResults>
          <ResultsBox>{results}</ResultsBox>
        </SearchAllResults>
      );
    } else if (ticketSearchError) {
      if (ticketSearchError === 'No Results Found') {
        return (
          <NoSearchResults
            title={'No results matched your search!'}
            subTitle={'Try searching with a different keyword.'}
          />
        );
      }

      return (
        <SearchAllResults>
          <ResultsBox>
            <SearchErrorWrapper key={'ticketSearchError'}>
              <ErrorMessage>{ticketSearchError}</ErrorMessage>
            </SearchErrorWrapper>
          </ResultsBox>
        </SearchAllResults>
      );
    }
  }

  renderSearchDisplay() {
    const { loading } = this.props;
    const results = this.renderResults();

    if (loading) {
      return (
        <LoadingContainer>
          <Loading delay={0} marginTop={'0px'} />
        </LoadingContainer>
      );
    } else if (!loading && !!results) {
      return results;
    }

    return (
      <NoSearchResults
        title={'Search historical tickets and macros'}
        subTitle={'Use keywords to find related results'}
      />
    );
  }

  render() {
    const { ticketSearchQuery, selectedSearchResult, selectedSearchResultType } = this.props;

    return selectedSearchResultType ? (
      <ResultPage
        resultType={selectedSearchResultType}
        query={ticketSearchQuery || this.state.autoSearchQuery}
        searchResult={selectedSearchResult}
        handlePartnerPlatformTicketSelection={this.handlePartnerPlatformTicketSelection}
      />
    ) : (
      <SearchOrdersPageWrapper>
        <BlueSection border={true}>
          <SearchAllBox className="page-content">
            <SearchInput
              defaultValue={ticketSearchQuery}
              placeholder="Search ticket keywords"
              messageImg={false}
              onClickFunction={(searchValue) => {
                this.handleSearchAll(searchValue);
                SessionAnalyticsService.event('Actions - Keyword Entered - Search All', { searchValue: searchValue });
              }}
            />
          </SearchAllBox>
        </BlueSection>
        {this.renderSearchDisplay()}
      </SearchOrdersPageWrapper>
    );
  }
}

SearchAll.propTypes = {
  selectedTicket: PropTypes.shape({
    ticketId: PropTypes.string.isRequired,
    associatedOrderNumber: PropTypes.number,
    simplrAccountPtrId: PropTypes.string.isRequired,
  }).isRequired,
  macroSearchResults: PropTypes.array,
  kbSearchResults: PropTypes.array,
  ticketSearchResults: PropTypes.array,
  selectedSearchResult: PropTypes.object,
  searchAll: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  const firstMessage = extractFirstMessage(state).messageBody;
  const loading = state.ui.loading.isLoading && state.ui.loading.component === 'searchPage';

  return {
    loading,
    firstMessage,
    selectedTicket: extractSelectedTicketFromState(state) || {},
    searchType: selectToolGeneralSearchType(state),
    macroSearchResults: getSearchAllMacroResults(state),
    kbSearchResults: getSearchAllKBResults(state),
    ticketSearchResults: getSearchAllTicketResults(state),
    ticketSearchQuery: getSearchAllQuery(state),
    ticketSearchError: getSearchAllError(state),
    selectedSearchResult: state.tools.ticketSearch.selectedResult.resultType ? getSelectedSearchResult(state) : {},
    selectedSearchResultType: state.tools.ticketSearch.selectedResult.resultType,
    isSpecialist: isSpecialist(state),
    noSearchAllResultsAndQueryIsEmpty: noSearchAllResultsAndQueryIsEmpty(state),
    partnerPlatformHistoricalTickets: getPartnerPlatformHistoricalTicketsFromState(state),
    isPartnerPlatformTicketsSearchEnabled: isPartnerPlatformTicketsSearchEnabled(state),
  };
}

const mapDispatchToProps = (dispatch) => ({
  setLoading: (isLoading) => {
    return dispatch(setLoading(isLoading, 'searchPage'));
  },
  setDefaultQueryForLDA: (query_string) => {
    return dispatch({
      type: 'SET_QUERY_FOR_LDA',
      query: query_string,
    });
  },
  ...bindActionCreators(
    {
      searchAll,
      searchInPartersTicketingSystem,
      selectParnerPlatformTicketForView,
      selectResult: selectSearchResultTicket,
    },
    dispatch
  ),
});

export default SearchAll = connect(mapStateToProps, mapDispatchToProps)(SearchAll);
